import { loadGoogleMapsMapLibrary } from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';

export type GeocodedLocation = {
  latitude: number;
  longitude: number;
  city?: string | null;
  stateCode?: string | null;
  zipcode?: string | null;
};

export type GeocodeLocationOptions = {
  countryCode?: string;
  acceptedTypes?: string[];
  cityTypes?: string[];
  stateTypes?: string[];
  zipCodeType?: string;
};

const DEFAULT_ACCEPTED_GEOCODER_TYPES: string[] = [
  'street_address',
  'route',
  'premise',
  'locality',
  'postal_code',
  'administrative_area_level_3',
];
const DEFAULT_CITY_TYPES = ['locality', 'postal_town'];
const DEFAULT_STATE_TYPES = ['administrative_area_level_1'];
const DEFAULT_ZIP_CODE_TYPE = 'postal_code';

/**
 * Trims a postal/ZIP value. Keeps any non-empty value as-is.
 */
export const normalizeUsZipCode = (raw?: string | null): string | null => {
  if (raw === undefined || raw === null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed || null;
};

/**
 * City, state code, and postal code from a geocoder result.
 * Component types come from options when provided.
 */
export const parseLocalityFromResult = (
  result?: google.maps.GeocoderResult,
  options?: Pick<GeocodeLocationOptions, 'cityTypes' | 'stateTypes' | 'zipCodeType'>
): { city: string | null; stateCode: string | null; zipcode: string | null } => {
  const components = result?.address_components ?? [];
  const findComponent = (type: string) =>
    components.find((component) => component.types.includes(type));

  const cityTypes =
    options?.cityTypes && options.cityTypes.length > 0 ? options.cityTypes : DEFAULT_CITY_TYPES;
  const stateTypes =
    options?.stateTypes && options.stateTypes.length > 0 ? options.stateTypes : DEFAULT_STATE_TYPES;
  const zipCodeType = options?.zipCodeType?.trim() || DEFAULT_ZIP_CODE_TYPE;

  const findFirstByTypes = (types: string[]) => {
    for (const type of types) {
      const component = findComponent(type);
      if (component) {
        return component;
      }
    }
    return undefined;
  };

  return {
    city: findFirstByTypes(cityTypes)?.long_name ?? null,
    stateCode: findFirstByTypes(stateTypes)?.short_name ?? null,
    zipcode: normalizeUsZipCode(findComponent(zipCodeType)?.long_name ?? null),
  };
};

/** International postal-ish tokens: alphanumeric with optional spaces/hyphens, length 2–10. */
const POSTAL_CODE_REGEX = /^[A-Za-z0-9][A-Za-z0-9\s-]{0,9}$/;
const CITY_NAME_REGEX = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
const STATE_NAME_REGEX = /^[A-Za-z]+(?:\s[A-Za-z]+)*$/;
const GENERAL_LOCATION_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9\s,'-]{2,}$/;

const ensureGoogleMapsLoaded = async (mapApiKey?: string): Promise<void> => {
  await loadGoogleMapsMapLibrary(mapApiKey);
};

/**
 * Performs a lightweight, local format check on a raw location query.
 */
export const isValidLocationQuery = (query: string): boolean => {
  const trimmed = query?.trim();

  if (!trimmed) {
    return false;
  }

  return (
    POSTAL_CODE_REGEX.test(trimmed) ||
    CITY_NAME_REGEX.test(trimmed) ||
    STATE_NAME_REGEX.test(trimmed) ||
    GENERAL_LOCATION_REGEX.test(trimmed)
  );
};

const hasAcceptedGeocoderType = (types: string[] | undefined, acceptedTypes: string[]): boolean => {
  if (!types || types.length === 0) {
    return false;
  }

  return types.some((type) => acceptedTypes.includes(type));
};

/**
 * Reverse-geocodes coordinates to a postal code when Google returns one.
 */
export const reverseGeocodeZipcode = async (
  latitude: number,
  longitude: number,
  mapApiKey?: string,
  options?: GeocodeLocationOptions
): Promise<string | null> => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  await ensureGoogleMapsLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        resolve(parseLocalityFromResult(results[0], options).zipcode);
        return;
      }
      resolve(null);
    });
  });
};

/**
 * Ensures a geocoded location has a postal code when resolvable via reverse geocode.
 */
export const withResolvedZipcode = async (
  location: GeocodedLocation,
  mapApiKey?: string,
  options?: GeocodeLocationOptions
): Promise<GeocodedLocation> => {
  const existing = normalizeUsZipCode(location.zipcode);
  if (existing) {
    return { ...location, zipcode: existing };
  }

  const zipcode = await reverseGeocodeZipcode(
    location.latitude,
    location.longitude,
    mapApiKey,
    options
  );
  return { ...location, zipcode };
};

/**
 * Geocodes an address string to coordinates.
 * Country restriction applies only when options.countryCode is set.
 */
export const geocodeLocation = async (
  address: string,
  mapApiKey?: string,
  options?: GeocodeLocationOptions
): Promise<GeocodedLocation | null> => {
  const trimmed = address?.trim();
  if (!trimmed || !isValidLocationQuery(trimmed)) {
    return null;
  }

  const acceptedTypes =
    options?.acceptedTypes && options.acceptedTypes.length > 0
      ? options.acceptedTypes
      : DEFAULT_ACCEPTED_GEOCODER_TYPES;
  const countryCode = options?.countryCode?.trim();

  await ensureGoogleMapsLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  const geocodeRequest: google.maps.GeocoderRequest = { address: trimmed };
  if (countryCode) {
    geocodeRequest.componentRestrictions = { country: countryCode };
  }

  const geocoded = await new Promise<GeocodedLocation | null>((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(geocodeRequest, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const firstResult = results[0];

        if (
          firstResult.partial_match ||
          !hasAcceptedGeocoderType(firstResult.types, acceptedTypes)
        ) {
          resolve(null);
          return;
        }

        const location = firstResult?.geometry?.location;
        const latitude = location?.lat();
        const longitude = location?.lng();

        if (typeof latitude === 'number' && typeof longitude === 'number') {
          resolve({
            latitude,
            longitude,
            ...parseLocalityFromResult(firstResult, options),
          });
          return;
        }
      }
      resolve(null);
    });
  });

  if (!geocoded) {
    return null;
  }

  return withResolvedZipcode(geocoded, mapApiKey, options);
};
